import React, { useState, useEffect } from "react";
import {
  TrashIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { investorsService } from "../services/investorsService";

const BulkDeleteBySchemeModal = ({
  isOpen,
  onClose,
  schemes,
  onDeleteComplete,
}) => {
  const [selectedScheme, setSelectedScheme] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingCount, setIsFetchingCount] = useState(false);
  const [schemeStats, setSchemeStats] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [fetchError, setFetchError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedScheme("");
      setDeleteReason("");
      setSchemeStats(null);
      setConfirmText("");
      setFetchError(null);
    }
  }, [isOpen]);

  // Fetch investor count when scheme is selected
  useEffect(() => {
    const fetchCount = async () => {
      if (!selectedScheme) {
        setSchemeStats(null);
        setFetchError(null);
        return;
      }

      console.log("Fetching count for scheme ID:", selectedScheme);
      setIsFetchingCount(true);
      setFetchError(null);
      try {
        const stats = await investorsService.getInvestorCountByScheme(selectedScheme);
        console.log("Received stats:", stats);
        setSchemeStats(stats);
      } catch (error) {
        console.error("Error fetching scheme count:", error);
        console.error("Error response:", error.response);
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        setSchemeStats(null);
        // Build more informative error message
        let errorMessage = "Failed to fetch investor count.";
        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
          errorMessage = "Network error. Please check if the server is running.";
        } else if (error.response?.status === 403) {
          errorMessage = error.response?.data?.message || "Access denied. You don't have permission.";
          if (error.response?.data?.required_permission) {
            errorMessage += ` Required: ${error.response.data.required_permission}`;
          }
        } else if (error.response?.status === 401) {
          errorMessage = "Session expired. Please login again.";
        } else if (error.response?.status === 500) {
          errorMessage = "Server error: " + (error.response?.data?.message || "Internal server error");
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        setFetchError(errorMessage);
      } finally {
        setIsFetchingCount(false);
      }
    };

    fetchCount();
  }, [selectedScheme]);

  const handleSoftDelete = async () => {
    if (!selectedScheme || !schemeStats || schemeStats.count === 0) return;

    setIsLoading(true);
    try {
      await investorsService.bulkDeleteByScheme(
        selectedScheme,
        "soft",
        deleteReason.trim() || null
      );
      onDeleteComplete();
      onClose();
    } catch (error) {
      console.error("Error in bulk soft delete:", error);
      alert(error.response?.data?.message || "Failed to delete investors");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedScheme || !schemeStats || schemeStats.count === 0) return;

    const schemeName = getSelectedSchemeName();
    if (confirmText !== schemeName) {
      alert(`Please type "${schemeName}" to confirm permanent deletion`);
      return;
    }

    setIsLoading(true);
    try {
      await investorsService.bulkDeleteByScheme(
        selectedScheme,
        "permanent",
        null
      );
      onDeleteComplete();
      onClose();
    } catch (error) {
      console.error("Error in bulk permanent delete:", error);
      alert(error.response?.data?.message || "Failed to delete investors");
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedSchemeName = () => {
    const scheme = schemes.find((s) => s.id === parseInt(selectedScheme));
    return scheme?.name || "";
  };

  // Show all schemes - let the user choose any scheme
  // The API will return 0 count if no investors exist for the selected scheme
  const selectableSchemes = schemes || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Bulk Delete by Scheme
              </h2>
              <p className="text-sm text-gray-500">
                Delete all investors from a specific scheme
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* No schemes available message */}
          {(!schemes || schemes.length === 0) && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                <span className="text-yellow-800">
                  No schemes available. Please add schemes first.
                </span>
              </div>
            </div>
          )}

          {/* Scheme Selection */}
          {schemes && schemes.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Scheme
            </label>
            <select
              value={selectedScheme}
              onChange={(e) => setSelectedScheme(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              <option value="">-- Select a scheme --</option>
              {selectableSchemes.map((scheme) => (
                <option key={scheme.id} value={scheme.id}>
                  {scheme.name} ({scheme.code})
                </option>
              ))}
            </select>
          </div>
          )}

          {/* Stats Preview */}
          {isFetchingCount && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-gray-600">Loading investor count...</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {fetchError && !isFetchingCount && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{fetchError}</span>
              </div>
            </div>
          )}

          {schemeStats && !isFetchingCount && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <UserGroupIcon className="h-5 w-5 text-gray-600" />
                <span className="font-medium text-gray-900">
                  Investors to be deleted:
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Investors:</span>
                  <span className="ml-2 font-bold text-gray-900">
                    {schemeStats.count}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Total USDT:</span>
                  <span className="ml-2 font-bold text-gray-900">
                    ${schemeStats.totalUSDT.toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">With UNS:</span>
                  <span className="ml-2 font-bold text-yellow-600">
                    {schemeStats.withUNS}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Returned:</span>
                  <span className="ml-2 font-bold text-green-600">
                    {schemeStats.returned}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* No investors message */}
          {schemeStats && schemeStats.count === 0 && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                <span className="text-yellow-800">
                  No investors found for this scheme.
                </span>
              </div>
            </div>
          )}

          {/* Delete Options - Only show if there are investors */}
          {schemeStats && schemeStats.count > 0 && (
            <div className="space-y-4">
              {/* Soft Delete Option */}
              <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-start gap-3">
                  <ArchiveBoxIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-1">
                      Move to Deleted Records
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      All {schemeStats.count} investor(s) will be moved to
                      deleted records and can be restored later.
                    </p>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-blue-800 mb-1">
                        Reason for deletion (optional)
                      </label>
                      <textarea
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows="2"
                        placeholder="Enter reason for bulk deletion..."
                        disabled={isLoading}
                      />
                    </div>

                    <button
                      onClick={handleSoftDelete}
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading
                        ? "Deleting..."
                        : `Move ${schemeStats.count} Investor(s) to Deleted Records`}
                    </button>
                  </div>
                </div>
              </div>

              {/* Permanent Delete Option */}
              <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                <div className="flex items-start gap-3">
                  <TrashIcon className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-900 mb-1">
                      Delete Permanently
                    </h4>
                    <p className="text-sm text-red-700 mb-3">
                      ⚠️ This action cannot be undone. All {schemeStats.count}{" "}
                      investor(s) will be permanently removed.
                    </p>

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-red-800 mb-1">
                        Type "{getSelectedSchemeName()}" to confirm
                      </label>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="w-full px-3 py-2 border border-red-300 rounded-md text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder={`Type scheme name to confirm...`}
                        disabled={isLoading}
                      />
                    </div>

                    <button
                      onClick={handlePermanentDelete}
                      disabled={
                        isLoading || confirmText !== getSelectedSchemeName()
                      }
                      className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading
                        ? "Permanently Deleting..."
                        : `Permanently Delete ${schemeStats.count} Investor(s)`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl sticky bottom-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-full px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteBySchemeModal;
